import React, { ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppstoreOutlined, EditOutlined } from '@ant-design/icons';
import { Flex } from 'antd';

import { EMPTY_ID } from '@totem/components/common/reference/ReferenceSelector';
import { ComponentInstance } from '@totem/components/customDashboard/home/lib/types';
import TitleEditModal from '@totem/components/customDashboard/home/TitleEditModal';
import InfoWidgetCard from '@totem/components/customDashboard/home/widgets/InfoWidgetCard';
import InfoWidgetRemoveButton from '@totem/components/customDashboard/home/widgets/InfoWidgetRemoveButton';
import WidgetContext from '@totem/components/customDashboard/home/widgets/WidgetContext';
import WidgetRemoveButton from '@totem/components/customDashboard/home/widgets/WidgetRemoveButton';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';

import '../home.css';

type Props = {
  children?: ReactNode;
  page?: string;
  pageNumber?: string;
  componentInstance: ComponentInstance;
  onRemoveItem?: (componentId: string) => void;
  onRenameItem?: (componentInstanceId: string, newTitle: string) => void;
};

const Widget = ({
  children,
  componentInstance,
  onRemoveItem,
  onRenameItem,
  page,
  pageNumber,
}: Props) => {
  const [title, setTitle] = useState<ReactNode>(<span />);
  const [showTitleEdit, setShowTitleEdit] = useState<boolean>(false);

  const widgetContextValue = useMemo(
    () => ({ componentInstance, onSetTitle: setTitle }),
    [componentInstance],
  );

  const editIcon = typeof onRenameItem === 'function' && (
    <EditOutlined
      style={{ marginLeft: '0.5rem', cursor: 'pointer', opacity: 0.5 }}
      onClick={() => setShowTitleEdit(true)}
    />
  );

  const getTitle = (instance: ComponentInstance) => {
    if (
      instance.componentInstanceId !== EMPTY_ID &&
      typeof page !== 'undefined' &&
      page !== null &&
      typeof pageNumber !== 'undefined' &&
      pageNumber !== null
    ) {
      return (
        <Flex justify={'flex-start'} align={'center'} gap={'middle'}>
          <AppstoreOutlined className={'dashboardWidgetDraggable'} />
          <Link
            to={`/dashboard/componentInstance/${page}/${pageNumber}/${instance.componentInstanceId}`}
            target={instance.componentInstanceId}
          >
            {instance.title}
          </Link>
          {editIcon}
        </Flex>
      );
    }
    return (
      <span>
        {instance.title}
        {editIcon}
      </span>
    );
  };

  const getWidgetContainer = (compInst: ComponentInstance) => {
    switch (compInst.style) {
      case 'Info Widget':
        return (
          <WidgetContext.Provider value={widgetContextValue}>
            <IBCard
              size={'small'}
              style={{ height: '100%' }}
              //styles={{ body: { flex: 1, overflow: 'auto', minHeight: 0 } }}
              title={getTitle(componentInstance)}
              extra={
                <Flex justify={'flex-end'} align={'center'}>
                  <div>{title !== null ? title : <span />}</div>
                  <div>
                    {typeof onRemoveItem === 'function' && (
                      <WidgetRemoveButton
                        onRemoveItem={onRemoveItem}
                        componentInstance={componentInstance}
                      />
                    )}
                  </div>
                </Flex>
              }
            >
              {children}
            </IBCard>
          </WidgetContext.Provider>
        );
      case 'Info Widget - Legacy':
        return (
          <InfoWidgetCard title={compInst.title}>
            <WidgetContext.Provider value={widgetContextValue}>
              {typeof onRemoveItem === 'function' && (
                <InfoWidgetRemoveButton
                  onRemoveItem={onRemoveItem}
                  componentInstance={componentInstance}
                />
              )}
              {children}
            </WidgetContext.Provider>
          </InfoWidgetCard>
        );
      case 'No Format':
        return (
          <WidgetContext.Provider value={widgetContextValue}>
            <IBCard style={{ height: '100%' }}>
              {typeof onRemoveItem === 'function' && (
                <WidgetRemoveButton
                  onRemoveItem={onRemoveItem}
                  componentInstance={componentInstance}
                />
              )}
              {children}
            </IBCard>
          </WidgetContext.Provider>
        );
      default:
        return (
          <WidgetContext.Provider value={widgetContextValue}>
            <IBCard
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              styles={{ body: { flex: 1, overflow: 'auto', minHeight: 0 } }}
              title={getTitle(componentInstance)}
              extra={
                <Flex justify={'flex-end'} align={'center'}>
                  <div>{title !== null ? title : <span />}</div>
                  <div>
                    {typeof onRemoveItem === 'function' && (
                      <WidgetRemoveButton
                        onRemoveItem={onRemoveItem}
                        componentInstance={componentInstance}
                      />
                    )}
                  </div>
                </Flex>
              }
            >
              {children}
            </IBCard>
          </WidgetContext.Provider>
        );
    }
  };

  return (
    <>
      {getWidgetContainer(componentInstance)}
      {showTitleEdit && (
        <TitleEditModal
          label="Edit Widget Title"
          title={componentInstance.title}
          visible={showTitleEdit}
          onSubmit={(newTitle) => {
            setShowTitleEdit(false);
            if (typeof onRenameItem === 'function') {
              onRenameItem(componentInstance.componentInstanceId, newTitle);
            }
          }}
          onClose={() => setShowTitleEdit(false)}
        />
      )}
    </>
  );
};

export default Widget;
